import Form from "@/components/Form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";

import { isRequired } from "@/lib/form";
import { ApiError } from "@/services/api";
import { UseMutateFunction } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { ReadProjectRole, WriteProjectRole } from "@/types/projectRoleType";
import { writeProjectRole } from "@/schemas/projectRole";
import { ReactNode, memo } from "react";
import { Input } from "@/components/ui/input";
import SaveButton from "@/components/SaveButton";
import PageContent from "@/components/PageContent";

interface ProjectRoleFormProps {
  data?: Partial<ReadProjectRole>;
  onSubmit: UseMutateFunction<ReadProjectRole, ApiError, WriteProjectRole>;
  disabled?: boolean;
  icon: ReactNode;
  title: string;
}

const defaultValues: Partial<ReadProjectRole> = {
  name: "",
  description: "",
};

const ProjectRoleFormComponent = ({
  data = defaultValues,
  onSubmit,
  disabled,
  icon,
  title,
}: ProjectRoleFormProps) => {
  const { t } = useTranslation();

  const form = useForm<WriteProjectRole>({
    resolver: zodResolver(writeProjectRole),
    defaultValues: {
      name: data?.name,
      description: data?.description ?? "",
    },
    disabled,
  });

  useFormUnsavedChangesWarning(form);

  return (
    <Form form={form} handleSubmit={onSubmit}>
      <PageContent icon={icon} title={title} footer={<SaveButton />}>
        <Fieldset className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Field
            name="name"
            label={t("projectRoles.fields.name")}
            required={isRequired(writeProjectRole.shape.name)}
            render={({ field }) => <Input className="w-full" {...field} />}
          />
          <Field
            name="description"
            label={t("projectRoles.fields.description")}
            required={isRequired(writeProjectRole.shape.description)}
            render={({ field }) => <Input className="w-full" {...field} />}
          />
        </Fieldset>
      </PageContent>
    </Form>
  );
};

export const ProjectRoleForm = memo(ProjectRoleFormComponent);
